import { useState } from "react";
import type { RequestDto } from "../lib/api";
import { displayTitle, formatDuration, formatMoney, thumbnailUrl } from "../lib/youtube";
import { useVideoTitle } from "../hooks/useVideoTitle";
import { useVideoDuration } from "../hooks/useVideoDuration";

/**
 * The head of the paid+free queue. Derived server-side, never stored — so
 * completing it automatically promotes whatever is next.
 */
export function NowPlaying({
  request,
  onComplete,
  onSkip,
  onPlayback,
  busy,
}: {
  request: RequestDto | null;
  onComplete: () => void;
  onSkip: () => void;
  /** Remote play/pause for the embed on the StreamView overlay (issue #9). */
  onPlayback?: (action: "play" | "pause") => void;
  busy?: boolean;
}) {
  // Hooks must run before the early return; harmless (resolve to null) when idle.
  const resolvedTitle = useVideoTitle(request?.youtubeUrl ?? "");
  const durationSeconds = useVideoDuration(request?.youtubeUrl ?? "");
  // Which command the button sends next — starts at "pause" since the embed
  // autoplays by default. The icon itself stays static (▶/⏸, not a toggling
  // single icon): nothing reports the embed's actual state back (see
  // notify.ts's OverlayControlEvent), so an icon that claims to reflect state
  // could easily just be wrong.
  const [nextAction, setNextAction] = useState<"play" | "pause">("pause");

  if (!request) {
    return (
      <div className="now">
        <div className="body">
          <div className="now-label">Now playing</div>
          <div className="muted">
            Queue is empty — the top of paid + free plays next.
          </div>
        </div>
      </div>
    );
  }

  const thumb = request.youtubeUrl ? thumbnailUrl(request.youtubeUrl) : null;
  const title = request.youtubeUrl
    ? resolvedTitle ?? displayTitle(request.youtubeUrl)
    : request.title ?? "Untitled request";

  return (
    <div className="now">
      <div className="thumb-wrap">
        {thumb ? <img className="thumb" src={thumb} alt="" /> : <div className="thumb" />}
        {durationSeconds !== null && (
          <span className="thumb-duration">{formatDuration(durationSeconds)}</span>
        )}
      </div>
      <div className="body">
        <div className="now-label">▶ Now playing</div>
        <div className="title">
          {request.youtubeUrl ? (
            <a href={request.youtubeUrl} target="_blank" rel="noreferrer">
              {title}
            </a>
          ) : (
            title
          )}
        </div>
        <div className="meta">
          <span className={`badge platform ${request.requester.platform}`}>
            {request.requester.platform}
          </span>
          <span>@{request.requester.username}</span>
          {request.matchedCents > 0 ? (
            <span className="badge amount">{formatMoney(request.matchedCents)}</span>
          ) : (
            <span className="badge free">free</span>
          )}
        </div>
      </div>
      <div className="actions">
        {onPlayback && request.youtubeUrl && (
          <button
            className="btn ghost icon playback-toggle"
            onClick={() => {
              onPlayback(nextAction);
              setNextAction(nextAction === "play" ? "pause" : "play");
            }}
            title="Play/pause the overlay video"
            aria-label="Play/pause the overlay video"
          >
            <span>▶</span>
            <span className="playback-toggle-slash" aria-hidden="true" />
            <span>⏸</span>
          </button>
        )}
        <button className="btn primary" onClick={onComplete} disabled={busy}>
          Done
        </button>
        <button className="btn danger" onClick={onSkip} disabled={busy}>
          Skip
        </button>
      </div>
    </div>
  );
}
